import {
  Dropdown,
  type ComboboxOpenChangeData,
  type ComboboxOpenEvents,
  type DropdownProps,
  type OptionOnSelectData,
  type SelectionEvents,
} from "@fluentui/react-components";
import { useControllableValue } from "@fluentui/react-hooks";
import * as React from "react";
import { useStyles } from "../PromptFilter.styles";
import { PromptFiltersSkeleton } from "../PromptFilterSkeleton";
import { usePromptFilterLocalizations } from "../usePromptFilterStrings";
import { groupFilters } from "../utils";
import type { FilterProps } from "./Filter.types";
import { RenderOptions } from "./RenderOptions";

export const DropdownFilter: React.FC<FilterProps> = ({
  appearance,
  tab,
  filter,
  isLoading,
  selectedValues,
  defaultSelectedValues,
  onChange,
  onOpenChanged,
}) => {
  const styles = useStyles(appearance)();
  const [values, setValues] = useControllableValue(
    selectedValues,
    defaultSelectedValues,
    onChange
  );
  const {
    facetKeyToLabel,
    facetValueToLabel,
    selectedValueToLabel,
    groupValueToLabel,
  } = usePromptFilterLocalizations();
  const filterLabel = facetKeyToLabel(filter.key);
  const filterByGroup = groupFilters(filter);
  const displayedValueText =
    values && values.length > 0
      ? values
          .map((value: string) => {
            const filterOption = filter.options.find(
              (option) => option.value === value
            );
            if (filterOption?.displayLabel) {
              return filterOption.displayLabel;
            }

            return selectedValueToLabel(value);
          })
          .join(", ")
      : "";

  const onChanged = (_: SelectionEvents, data: OptionOnSelectData): void => {
    setValues(data.selectedOptions);
  };

  const handleOnOpenChanged: DropdownProps["onOpenChange"] = React.useCallback(
    (_e: ComboboxOpenEvents, data: ComboboxOpenChangeData) => {
      onOpenChanged && onOpenChanged(data.open, [filter]);
    },
    [filter, onOpenChanged]
  );

  if (filter.options.length < 1) {
    return null;
  }

  return (
    <React.Fragment key={`${tab}-${filter.key}`}>
      {isLoading ? (
        <PromptFiltersSkeleton />
      ) : (
        <Dropdown
          data-testid={`${filter.key}-dropdown`}
          aria-label={filterLabel}
          className={styles.dropdown}
          multiselect={!filter.isSingleSelect}
          button={
            displayedValueText ? (
              <span className={styles.truncatedText}>{displayedValueText}</span>
            ) : (
              <span className={styles.placeholderText}>{filterLabel}</span>
            )
          }
          selectedOptions={values ?? []}
          onOptionSelect={onChanged}
          onOpenChange={handleOnOpenChanged}
          style={{ zIndex: 2 }} // Added z-index style so doesn't get hidden behind other elements
        >
          {Object.entries(filterByGroup).map(([groupKey, filterOptions]) => (
            <React.Fragment key={groupKey}>
              <RenderOptions
                groupKey={groupKey}
                groupLabel={groupValueToLabel(groupKey)}
                filterOptions={filterOptions}
                selectedValues={values ?? []}
                valueToLabel={facetValueToLabel}
                appearance={appearance}
              />
            </React.Fragment>
          ))}
        </Dropdown>
      )}
    </React.Fragment>
  );
};
